import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation, useNavigate } from 'react-router'
import { localizeError, type GameError } from '@/api/errors'
import { Button } from '@/components/Button'
import { firebaseConfigured, isMockMode } from '@/config/runtime'
import { signInDemo, signInWithEmail, signInWithGoogle, type SignInOutcome } from './actions'
import { AuthLayout } from './AuthLayout'
import { useSession } from './session'

type Busy = 'google' | 'email' | 'demo' | null

function GoogleGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden fill="currentColor">
      <path d="M21.6 12.2c0-.7-.1-1.4-.2-2H12v3.8h5.4a4.6 4.6 0 01-2 3v2.5h3.2c1.9-1.7 3-4.3 3-7.3zM12 22c2.7 0 5-.9 6.6-2.4l-3.2-2.5c-.9.6-2 1-3.4 1-2.6 0-4.8-1.8-5.6-4.1H3.1v2.6A10 10 0 0012 22zM6.4 14c-.2-.6-.3-1.3-.3-2s.1-1.4.3-2V7.4H3.1a10 10 0 000 9.2L6.4 14zM12 5.9c1.5 0 2.8.5 3.8 1.5l2.9-2.9A10 10 0 003.1 7.4L6.4 10c.8-2.4 3-4.1 5.6-4.1z" />
    </svg>
  )
}

export function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const status = useSession((s) => s.status)
  // Shown until the player acts: "you signed in somewhere else" explains a
  // login screen nobody asked for. A new attempt replaces it with its own outcome.
  const endedReason = useSession((s) => s.endedReason)
  const [busy, setBusy] = useState<Busy>(null)
  const [error, setError] = useState<GameError | null>(null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const mock = isMockMode()
  const canFirebase = firebaseConfigured()
  const from = (location.state as { from?: { pathname: string; search: string } } | null)?.from

  if (status === 'signedIn') {
    return <Navigate to={from ? `${from.pathname}${from.search}` : '/map'} replace />
  }

  async function run(kind: Exclude<Busy, null>, fn: () => Promise<SignInOutcome>) {
    setBusy(kind)
    setError(null)
    useSession.setState({ endedReason: null })
    const outcome = await fn()
    setBusy(null)
    if (outcome.ok) return
    if (outcome.redirect) {
      navigate(outcome.redirect, { replace: true })
      return
    }
    // Closing the Google popup is not an error worth a red line.
    if (outcome.error.code !== 'firebase_cancelled') setError(outcome.error)
  }

  function onEmail(e: FormEvent) {
    e.preventDefault()
    if (!email || !password) return
    void run('email', () => signInWithEmail(email.trim(), password))
  }

  return (
    <AuthLayout>
      <div className="soc-card relative p-7">
        <span aria-hidden className="absolute inset-x-0 top-0 h-[3px] bg-crimson" />
        <h1 className="soc-title text-[26px]">{t('auth.title')}</h1>
        <p className="mt-2 text-ink-soft">{t('auth.subtitle')}</p>

        {mock && (
          <div className="mt-6 border border-brass-dim p-4" style={{ background: 'color-mix(in srgb, var(--soc-brass) 6%, transparent)' }}>
            <p className="soc-label text-brass">{t('auth.demoTitle')}</p>
            <p className="mt-2 text-[14px] text-ink-soft">{t('auth.demoBody')}</p>
            <Button
              variant="primary"
              className="mt-4 w-full"
              disabled={busy !== null}
              onClick={() => void run('demo', signInDemo)}
              data-testid="demo-login"
            >
              {busy === 'demo' ? t('auth.signingIn') : t('auth.demoCta')}
            </Button>
          </div>
        )}

        {canFirebase ? (
          <>
            <Button
              variant={mock ? 'secondary' : 'primary'}
              className="mt-6 w-full"
              icon={<GoogleGlyph />}
              disabled={busy !== null}
              onClick={() => void run('google', signInWithGoogle)}
            >
              {busy === 'google' ? t('auth.signingIn') : t('auth.google')}
            </Button>
            <div className="my-6 flex items-center gap-3">
              <div className="soc-divider flex-1" />
              <span className="soc-label">{t('auth.or')}</span>
              <div className="soc-divider flex-1" />
            </div>
            <form onSubmit={onEmail} className="flex flex-col gap-4" noValidate>
              <label className="flex flex-col gap-1.5">
                <span className="soc-label">{t('auth.email')}</span>
                <input
                  className="soc-field"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="soc-label">{t('auth.password')}</span>
                <input
                  className="soc-field"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </label>
              <Button type="submit" variant="secondary" disabled={busy !== null || !email || !password}>
                {busy === 'email' ? t('auth.signingIn') : t('auth.emailCta')}
              </Button>
            </form>
          </>
        ) : (
          !mock && <p className="mt-6 border border-warning p-3 text-[14px] text-warning">{t('auth.notConfigured')}</p>
        )}

        {error && (
          <p role="alert" className="mt-5 border-l-[3px] border-danger bg-bg-deep px-3 py-2 text-[14px] text-ink">
            {error.message}
          </p>
        )}
        {!error && endedReason && (
          <p role="status" className="mt-5 border-l-[3px] border-warning bg-bg-deep px-3 py-2 text-[14px] text-ink">
            {localizeError(endedReason)}
          </p>
        )}
      </div>
      <p className="mt-4 text-[13px] text-ink-mute">{t('auth.existingOnly')}</p>
    </AuthLayout>
  )
}
